import { notifyProgress } from '@billy/design-system'
import { TFunction } from 'i18next'

import { DownloadFormatToMimeType } from '../../../../../../../constants/downloadFormatToMimeType'
import { DownloadFormat } from '../../../../../../../enums/downloadFormat'
import { SalesReportDateSelection } from '../../../../../../../enums/salesReportDateSelection'
import { fetchSalesOverviewReportDownloadFileData } from '../../../../../../../query-api'
import { SalesReportLocaleIdentifier } from '../../../../../../../types/salesReportLocaleIdentifier'
import { getSalesReportDateRange } from '../../../../../../../utils/getSalesReportDateRange'
import { SALES_REPORT_DOWNLOAD_PROGRESS_MAX } from '../../../constants/salesReportDownloadProgressMax'
import { DownloadWithProgress } from '../../../utils/downloadWithProgress'
import { getSalesReportDownloadNotificationId } from '../../../utils/getSalesReportDownloadNotificationId'

interface DownloadSalesOverviewReportFileProps {
  dateSelection: SalesReportDateSelection
  endDate?: string
  firstFiscalYearStart?: string
  format: DownloadFormat
  locale: SalesReportLocaleIdentifier
  onError?: () => void
  onSuccess?: () => void
  startDate?: string
  t: TFunction
}

export const downloadSalesOverviewReportFile = async ({
  dateSelection,
  endDate,
  firstFiscalYearStart,
  format,
  locale,
  onError,
  onSuccess,
  startDate,
  t,
}: DownloadSalesOverviewReportFileProps): Promise<void> => {
  const { startDate: salesReportStartDate, endDate: salesReportEndDate } = getSalesReportDateRange(
    startDate,
    endDate,
    firstFiscalYearStart,
  )
  const notificationId = getSalesReportDownloadNotificationId(format)

  if (!salesReportStartDate || !salesReportEndDate) {
    notifyProgress({
      id: notificationId,
      title: t('sales_report.download_error.missing_start_or_end_date'),
      variant: 'error',
      progress: SALES_REPORT_DOWNLOAD_PROGRESS_MAX,
      closable: true,
    })
    onError?.()

    return
  }

  const fetchExportData = () =>
    fetchSalesOverviewReportDownloadFileData({
      dateSelection,
      startDate: salesReportStartDate,
      endDate: salesReportEndDate,
      format,
      locale,
    })

  const downloader = new DownloadWithProgress({
    fetchExportData,
    mimeType: DownloadFormatToMimeType[format],
    notificationId,
    onError,
    onSuccess,
    t,
  })

  await downloader.start()
}
